/**
 * The shortlist panel and the header button that opens it.
 *
 * The shortlist is what stops the three discovery routes dead-ending: codes
 * found by search, by category or from a company name all land here, and one
 * action carries the whole set into the acquisition dialog in phase 4.
 *
 * Preview is a button rather than something that runs on open. It spends one
 * Companies House call per code out of a 600-per-5-minute budget, so it happens
 * when the operator asks for it.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { shortlistApi } from '../lib/api.js';
import { useShortlist } from '../lib/shortlist-context.jsx';
import { AcquisitionDialog, AcquisitionStarted } from './AcquisitionDialog.jsx';
import { IconBasket, IconClose, IconPlay, IconTrash } from './Icons.jsx';

export function ShortlistButton({ onOpen }) {
  const { count } = useShortlist();

  return (
    <button
      type="button"
      className="btn btn-ghost btn-sm shortlist-button tip tip-below tip-left"
      onClick={onOpen}
      data-tip={
        count === 0
          ? 'The shortlist is empty. Add SIC codes from a search, a category or a company lookup.'
          : `${count} code${count === 1 ? '' : 's'} shortlisted. Open to preview how many companies they would return.`
      }
    >
      <IconBasket />
      Shortlist
      {count > 0 ? <span className="shortlist-button-count">{count}</span> : null}
    </button>
  );
}

export function ShortlistPanel({ onClose }) {
  const { items, count, isLoading, remove, clear, refresh } = useShortlist();
  const [preview, setPreview] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [error, setError] = useState(null);

  // Phase 4: the acquisition dialog, opened for the whole basket at once.
  const [acquiring, setAcquiring] = useState(null);
  const [started, setStarted] = useState(null);
  const navigate = useNavigate();

  const runPreview = async () => {
    setIsPreviewing(true);
    setError(null);
    try {
      setPreview(await shortlistApi.preview(['active']));
    } catch (caught) {
      setError(caught.message);
    } finally {
      setIsPreviewing(false);
    }
  };

  const countsByCode = new Map((preview?.codes ?? []).map((entry) => [entry.sicCode, entry]));

  // Escape closes the topmost overlay only: skipped while the acquisition
  // dialog (or its confirmation) is open on top of this panel, since each of
  // those already closes itself on the same keypress - without the guard,
  // one Escape would close both at once.
  useEffect(() => {
    if (acquiring || started) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [acquiring, started, onClose]);

  return (
    <>
      <div className="scrim" onClick={onClose} role="presentation" />
      <aside className="shortlist-panel" aria-label="Shortlist">
        <header className="shortlist-panel-header">
          <span className="shortlist-panel-title">Shortlist</span>
          <span className="badge">{count}</span>
          <button
            type="button"
            className="btn btn-ghost btn-icon"
            onClick={onClose}
            aria-label="Close the shortlist"
          >
            <IconClose />
          </button>
        </header>

        <div className="shortlist-panel-body">
          {error ? (
            <div className="card-body">
              <div className="alert alert-error">
                <span className="alert-body">{error}</span>
              </div>
            </div>
          ) : null}

          {isLoading && count === 0 ? (
            <div className="loading-panel">
              <span className="spinner" />
              Loading
            </div>
          ) : count === 0 ? (
            <div className="empty">
              <h3 className="empty-title">Nothing shortlisted yet</h3>
              <p className="empty-description">
                Press <strong>Shortlist</strong> on any code - from the search results, a category
                card, or the codes a company files - and it collects here. The list survives a
                refresh.
              </p>
            </div>
          ) : (
            items.map((item) => {
              const counted = countsByCode.get(item.sicCode);
              return (
                <div className="shortlist-item" key={item.sicCode}>
                  <span className="code-row-number">{item.sicCode}</span>
                  <span className="code-row-body">
                    <span className="code-row-description">
                      {item.code?.description ?? 'Unknown code'}
                    </span>
                    <span className="code-row-path">
                      added from {item.source}
                      {item.sourceDetail ? ` · ${item.sourceDetail}` : ''}
                    </span>
                    {counted ? (
                      <span className="code-row-reason">
                        {counted.companyCount === null ? (
                          <span className="value-absent">count unavailable</span>
                        ) : (
                          <>
                            <em>{counted.companyCount.toLocaleString()}</em> active companies
                          </>
                        )}
                      </span>
                    ) : null}
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-icon btn-sm tip tip-left"
                    onClick={() => void remove(item.sicCode)}
                    data-tip="Remove from the shortlist"
                    aria-label={`Remove ${item.sicCode}`}
                  >
                    <IconTrash />
                  </button>
                </div>
              );
            })
          )}
        </div>

        {count > 0 ? (
          <footer className="shortlist-panel-footer">
            {preview ? (
              <div className="shortlist-total">
                <span>
                  Active companies across {preview.codes.length} code
                  {preview.codes.length === 1 ? '' : 's'}
                  <span
                    className="info-tip tip"
                    data-tip={
                      preview.totalIsUpperBound
                        ? 'An upper bound: a company filing two shortlisted codes is counted under both, and the de-duplicated figure could not be fetched this time.'
                        : 'De-duplicated: a company filing two shortlisted codes is counted once, the same as the acquisition would store it.'
                    }
                  >
                    i
                  </span>
                </span>
                <strong>{preview.total.toLocaleString()}</strong>
              </div>
            ) : null}

            {preview?.unavailable ? (
              <span className="text-xs text-warn">
                {preview.unavailable} code
                {preview.unavailable === 1 ? '' : 's'} could not be counted - Companies House did not
                answer.
              </span>
            ) : null}

            <div className="row">
              <button
                type="button"
                className="btn tip"
                onClick={() => void runPreview()}
                disabled={isPreviewing}
                data-tip={`Runs one cheap size=1 count per code against Companies House. Costs ${count} API call${count === 1 ? '' : 's'} out of 600 per 5 minutes.`}
              >
                {isPreviewing ? <span className="spinner" /> : null}
                Preview all
              </button>

              <button
                type="button"
                className="btn btn-primary tip"
                onClick={() => setAcquiring(items.map((item) => item.sicCode))}
                disabled={count === 0}
                data-tip="Opens the acquisition dialog for every shortlisted code. Each code runs as its own job, one after another, so they cannot compete for the rate limit."
              >
                <IconPlay />
                Acquire all
              </button>

              <span className="spacer" />

              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  void clear();
                  setPreview(null);
                }}
              >
                Clear
              </button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => void refresh()}>
                Refresh
              </button>
            </div>
          </footer>
        ) : null}
      </aside>

      {acquiring ? (
        <AcquisitionDialog
          codes={acquiring}
          // Called with the queued runs when Start was pressed, with nothing
          // when the dialog was simply dismissed.
          onClose={(result) => {
            setAcquiring(null);
            if (result) setStarted(result);
          }}
        />
      ) : null}

      {started ? (
        <AcquisitionStarted
          result={started}
          onClose={() => setStarted(null)}
          onView={() => {
            setStarted(null);
            onClose();
            navigate('/log');
          }}
        />
      ) : null}
    </>
  );
}
